import React from "react";
import { Helmet } from "react-helmet-async";
import { getPublicBaseUrl } from "../utils/publicBaseUrl";
import {
  DEFAULT_SOCIAL_DESCRIPTION,
  DEFAULT_SOCIAL_TITLE,
  OG_IMAGE,
} from "../utils/socialMeta";
import { capMetaDescription } from "../utils/metaDescription";

/**
 * SEO Component for comprehensive metadata management
 * Supports: Meta tags, Open Graph, Twitter Cards, JSON-LD structured data
 * OG tags follow LinkedIn best practice: absolute image URL, dimensions, alt, og:url.
 */
const SEO = ({
  title = DEFAULT_SOCIAL_TITLE,
  description = DEFAULT_SOCIAL_DESCRIPTION,
  // `keywords` is still accepted from call sites but intentionally not
  // rendered: search engines ignore meta keywords, and it only exposes
  // targeting to competitors.
  // eslint-disable-next-line no-unused-vars
  keywords = null,
  image = OG_IMAGE.path,
  imageAlt = null,
  url = "",
  type = "website",
  siteName = "Jamil Global",
  locale = "en_US",
  alternateLocales = [],
  author = "Danyal Jamil",
  publishedTime = null,
  modifiedTime = null,
  structuredData = null,
  canonicalUrl = null,
  noindex = false,
  nofollow = false,
  geoRegion = "KW",
  geoPlacename = "Kuwait City",
  geoPosition = "29.3759;47.9774",
}) => {
  // Default base URL - update with your actual domain
  const baseUrl = getPublicBaseUrl();
  const metaDescription = capMetaDescription(description);
  const normalizePath = (value = "") => {
    if (!value) return "/";
    let normalized = String(value).trim();
    if (normalized === "/") return "/";
    if (/^https?:\/\//i.test(normalized)) {
      try {
        normalized = new URL(normalized).pathname || "/";
      } catch {
        normalized = normalized.replace(/^https?:\/\/[^/]+/, "");
      }
    }

    normalized = normalized.split("?")[0].split("#")[0];
    if (!normalized.startsWith("/")) normalized = `/${normalized}`;
    return normalized || "/";
  };

  const canonicalPath = normalizePath(url);
  const canonicalPathValue = canonicalUrl
    ? normalizePath(canonicalUrl)
    : canonicalPath;
  const canonical = `${baseUrl}${
    canonicalPathValue === "/" ? "" : canonicalPathValue
  }`;
  const fullUrl = canonical;
  const fullImageUrl = image.startsWith("http") ? image : `${baseUrl}${image}`;
  const resolvedImageAlt =
    imageAlt ||
    (fullImageUrl.endsWith(OG_IMAGE.path) || fullImageUrl.endsWith("/og-image.png")
      ? OG_IMAGE.alt
      : undefined);
  const includeOgImageDims =
    fullImageUrl.endsWith(OG_IMAGE.path) || fullImageUrl.endsWith("/og-image.png");
  const resolveLocaleMeta = (localeValue) => {
    if (typeof localeValue === "string") {
      return {
        locale: localeValue,
      };
    }

    if (
      typeof localeValue === "object" &&
      localeValue !== null &&
      typeof localeValue.lang === "string"
    ) {
      return {
        locale: localeValue.lang,
        href: localeValue.href,
      };
    }

    return null;
  };

  const normalizeLocale = (locale) => {
    const value = (locale || "").trim();
    const [lang = "", region = ""] = value.split(/[-_]/);
    const lowerLang = lang.toLowerCase();
    const upperRegion = region.toUpperCase();
    const normalizedHrefLang =
      upperRegion && lowerLang ? `${lowerLang}-${upperRegion}` : value;

    const normalizedOgLocale =
      upperRegion && lowerLang ? `${lowerLang}_${upperRegion}` : value;

    return {
      hrefLang: normalizedHrefLang,
      ogLocale: normalizedOgLocale,
      pathSegment: normalizedHrefLang.toLowerCase(),
    };
  };

  const resolvedAlternateLocales = (
    Array.isArray(alternateLocales) ? alternateLocales : []
  )
    .map(resolveLocaleMeta)
    .filter((loc) => loc && typeof loc.href === "string" && loc.href.trim())
    .map((localeMeta) => {
      const { hrefLang, ogLocale } = normalizeLocale(localeMeta.locale);

      if (!hrefLang) return null;

      return {
        hrefLang,
        hrefLocale: ogLocale,
        href: localeMeta.href,
      };
    })
    .filter(Boolean);

  const hasEnglishAlternate = resolvedAlternateLocales.some((loc) =>
    loc.hrefLang.toLowerCase().startsWith("en")
  );

  // Build robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push("noindex");
  else robotsContent.push("index");
  if (nofollow) robotsContent.push("nofollow");
  else robotsContent.push("follow");

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent.join(", ")} />
      <link rel="canonical" href={canonical} />

      {/* Geographic Targeting */}
      {geoRegion && <meta name="geo.region" content={geoRegion} />}
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}
      {geoPosition && <meta name="geo.position" content={geoPosition} />}
      {geoPosition && (
        <meta
          name="ICBM"
          content={geoPosition.includes(";") ? geoPosition.replace(";", ", ") : geoPosition}
        />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={fullImageUrl} />
      {includeOgImageDims && (
        <>
          <meta property="og:image:width" content={String(OG_IMAGE.width)} />
          <meta property="og:image:height" content={String(OG_IMAGE.height)} />
        </>
      )}
      {resolvedImageAlt && (
        <meta property="og:image:alt" content={resolvedImageAlt} />
      )}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      {resolvedAlternateLocales.map((loc) => (
        <meta
          key={`${loc.hrefLang}-og-alt`}
          property="og:locale:alternate"
          content={loc.hrefLocale || loc.hrefLang.replace("-", "_")}
        />
      ))}
      {!hasEnglishAlternate &&
        locale !== "en_US" &&
        locale !== "en_US_POSIX" && (
          <meta property="og:locale:alternate" content="en_US" />
        )}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      {resolvedImageAlt && (
        <meta name="twitter:image:alt" content={resolvedImageAlt} />
      )}

      {/* Language Alternates */}
      {resolvedAlternateLocales.length > 0 && (
        <>
          {!hasEnglishAlternate && (
            <link rel="alternate" hrefLang="en" href={canonical} />
          )}
          {resolvedAlternateLocales.map((loc) => (
            <link
              key={loc.hrefLang + loc.href}
              rel="alternate"
              hrefLang={loc.hrefLang}
              href={loc.href}
            />
          ))}
          <link rel="alternate" hrefLang="x-default" href={canonical} />
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
